import { useEffect, useState } from 'react';

/** In our app, we set pointer-events to none on the body when a modal is open.
 *
 *  This hook detects any time a class or style changes on the body via the MutationObserver,
 *  and triggers a callback. This callback both checks the window.getComputedStyle of the body,
 *  and updates the state of isPointerBlocked accordingly.
 *
 *  The observer is disconnected on dismount.
 *
 * @returns {boolean} isPointerBlocked - True if any modal is open, false otherwise.
 */
export const useIsAnyModalOpen = (): boolean => {
  const [isPointerBlocked, setIsPointerBlocked] = useState(false);

  useEffect(() => {
    // ==== Callback to check if pointer events are blocked ====
    const handlePointerEvents = () => {
      const bodyStyle = window.getComputedStyle(document.body);
      const bodyClassList = document.body.classList;

      setIsPointerBlocked(
        // pointerEvents: none is sufficient for most modals, but the Savings Calculator(TM)
        // coach mark uses touchAction: none instead, and does pretty much the same thing.
        //
        // class box_active_disable_scrolling can get added by digioh when it opens a modal
        // and it also sets touchAction to none, so we need to exclude it from our check.
        bodyStyle?.pointerEvents === 'none' ||
          (bodyStyle?.touchAction === 'none' &&
            !bodyClassList?.contains('box_active_disable_scrolling'))
      );
    };

    // ==== Initial check only on mount ====
    handlePointerEvents();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === 'attributes' &&
          (mutation.attributeName === 'style' || mutation.attributeName === 'class')
        ) {
          handlePointerEvents();
        }
      });
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    // ==== Cleanup ====
    return () => {
      observer.disconnect();
    };
  }, []);

  return isPointerBlocked;
};
